import { useEffect, useRef, useState } from "react"
import { Icon } from "~/react/ui/icon"
import { cn } from "~/lib/utils"

export type SaveIndicatorState = "default" | "saving" | "saved" | "error"

const MIN_SAVING_VISIBLE_MS = 200

// Used to compute “remaining time” for the 200ms minimum spinner visibility (no extra delay on slow saves).
function getNowMs(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") return performance.now()
  return Date.now()
}

function userPrefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function renderIconForState(state: SaveIndicatorState) {
  switch (state) {
    case "saving":
      return <Icon icon="spinner-third" className="follow-up-editor__icon fa-spin" size="sm" aria-hidden="true" />
    case "saved":
      return (
        <Icon
          icon="envelope-circle-check"
          className="follow-up-editor__icon text-success"
          size="sm"
          aria-hidden="true"
        />
      )
    case "error":
      return (
        <Icon icon="exclamation-triangle" className="follow-up-editor__icon text-danger" size="sm" aria-hidden="true" />
      )
    default:
      return <Icon icon="envelope" className="follow-up-editor__icon" size="sm" aria-hidden="true" />
  }
}

export function SaveIndicatorIcon({ state }: { state: SaveIndicatorState }) {
  // What is currently rendered (icon)
  const [displayState, setDisplayState] = useState<SaveIndicatorState>(state)
  // Visual transition phase applied via CSS classes
  const [phase, setPhase] = useState<"idle" | "out" | "in">("idle")

  // When the spinner became visible (to enforce minimum spinner duration)
  const savingStartedAtRef = useRef<number | null>(null)
  // The next state we want to show after fade-out completes
  const targetStateRef = useRef<SaveIndicatorState | null>(null)
  // Single pending timeout (min spinner delay / deferred fade-out start)
  const timeoutIdRef = useRef<number | null>(null)

  const clearPendingTimeout = () => {
    if (timeoutIdRef.current !== null) {
      window.clearTimeout(timeoutIdRef.current)
      timeoutIdRef.current = null
    }
  }

  // Track when the spinner actually becomes visible
  useEffect(() => {
    if (displayState === "saving") {
      savingStartedAtRef.current = getNowMs()
    }
  }, [displayState])

  // Drive state transitions from the incoming prop `state`
  useEffect(() => {
    if (state === displayState) return

    clearPendingTimeout()

    const reducedMotion = userPrefersReducedMotion()

    // Always enter the saving state immediately (avoid hiding the spinner behind an exit animation).
    if (state === "saving") {
      targetStateRef.current = null
      setDisplayState("saving")
      setPhase(reducedMotion ? "idle" : "in")
      return
    }

    const minSpinnerDelayMs = (() => {
      if (displayState !== "saving") return 0
      const startedAt = savingStartedAtRef.current
      if (startedAt === null) return 0
      const elapsed = getNowMs() - startedAt
      return Math.max(0, MIN_SAVING_VISIBLE_MS - elapsed)
    })()

    // Reduced motion: keep minimum spinner visibility, but switch without animation.
    if (reducedMotion) {
      if (minSpinnerDelayMs === 0) {
        setDisplayState(state)
      } else {
        timeoutIdRef.current = window.setTimeout(() => {
          setDisplayState(state)
        }, minSpinnerDelayMs)
      }
      targetStateRef.current = null
      setPhase("idle")
      return
    }

    // Animated: swap icon only after fade-out completes.
    targetStateRef.current = state

    // If we're already fading out, just update the target; the animationend handler will pick it up.
    if (phase === "out") return

    timeoutIdRef.current = window.setTimeout(() => {
      setPhase("out")
    }, minSpinnerDelayMs)

    return clearPendingTimeout
  }, [state, displayState, phase])

  const handleAnimationEnd = (e: React.AnimationEvent<HTMLSpanElement>) => {
    // Ignore bubbled events from children; only react to wrapper animations.
    if (e.currentTarget !== e.target) return

    if (e.animationName === "follow-up-editor-save-indicator-out" && phase === "out") {
      const nextState = targetStateRef.current
      targetStateRef.current = null
      if (nextState) {
        setDisplayState(nextState)
        setPhase("in")
      } else {
        setPhase("idle")
      }
      return
    }

    if (e.animationName === "follow-up-editor-save-indicator-in" && phase === "in") {
      setPhase("idle")
    }
  }

  return (
    <span
      className={cn(
        "follow-up-editor__save-indicator",
        phase === "out" && "is-transitioning-out",
        phase === "in" && "is-transitioning-in",
      )}
      data-save-indicator-state={displayState}
      data-save-indicator-phase={phase}
      onAnimationEnd={handleAnimationEnd}
    >
      {renderIconForState(displayState)}
    </span>
  )
}
